import { CT } from "./lib/tokens";
import { KIND_TO_DEFAULT } from "./utils/taxonomyMap";
import { resolveTaxonomyEntry, resolveTaxonomyLabel } from "./lib/taxonomyHelpers";
import { taxonomyEntriesOf } from "./lib/taxonomyRegistry";
import type { TaxValue } from "./lib/taxonomyTypes";
export const I = { s: 12, m: 14, l: 16 };
export const scrollToEv = (evId: any) => {
  console.log("scrollToEv called with evId=", evId);
  const tryScroll = (attempt: any) => {
    const el = document.getElementById("ev-" + evId);
    console.log("scrollToEv attempt", attempt, "id=ev-" + evId, "found=", !!el);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.remove("evHl");
      void el.offsetWidth;
      el.classList.add("evHl");
      console.log("scrollToEv SUCCESS - scrolled and highlighted");
    } else if (attempt < 4) {
      setTimeout(
        () => tryScroll(attempt + 1),
        attempt === 0 ? 200 : attempt === 1 ? 500 : attempt === 2 ? 1000 : 1500,
      );
    } else {
      console.log("scrollToEv FAILED - element not found after 4 attempts");
    }
  };
  setTimeout(() => tryScroll(0), 200);
};
// 109 §7f — scrollToEv's sibling for stage rows (`#stg-{id}`, same .evHl
// flash, same retry ladder). Lives here beside it rather than as a copy in a
// view, per the brief.
export const scrollToStage = (stageId: any) => {
  const tryScroll = (attempt: any) => {
    const el = document.getElementById("stg-" + stageId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.remove("evHl");
      void el.offsetWidth;
      el.classList.add("evHl");
    } else if (attempt < 4) {
      setTimeout(
        () => tryScroll(attempt + 1),
        attempt === 0 ? 200 : attempt === 1 ? 500 : attempt === 2 ? 1000 : 1500,
      );
    }
  };
  setTimeout(() => tryScroll(0), 200);
};
export const queueScrollToEv = (evId: any) => {
  console.log("queueScrollToEv storing evId=", evId);
  try {
    localStorage.setItem("lx:scrollToEvent", String(evId));
  } catch {}
};
export const checkScrollToEv = () => {
  try {
    const evId = localStorage.getItem("lx:scrollToEvent");
    console.log("checkScrollToEv read=", evId);
    if (evId) {
      localStorage.removeItem("lx:scrollToEvent");
      scrollToEv(+evId || evId);
    }
  } catch {}
};
// Phase 19 r9 — non-consuming peek at the queued scroll-to-event id. Lets a
// view (CaseView / CollectiveCaseView) prepare its agenda list (clear the
// week filter, jump to the event's page) so the target #ev-<id> is actually
// rendered BEFORE checkScrollToEv() runs and removes the key.
export const peekScrollToEv = () => {
  try {
    const evId = localStorage.getItem("lx:scrollToEvent");
    return evId ? (+evId || evId) : null;
  } catch {
    return null;
  }
};
export const scrollToTk = (tkId: any) => {
  const tryScroll = (attempt: any) => {
    const el = document.getElementById("tk-" + tkId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.remove("evHl");
      void el.offsetWidth;
      el.classList.add("evHl");
    } else if (attempt < 4) {
      setTimeout(
        () => tryScroll(attempt + 1),
        attempt === 0 ? 200 : attempt === 1 ? 500 : attempt === 2 ? 1000 : 1500,
      );
    }
  };
  setTimeout(() => tryScroll(0), 200);
};
export const queueScrollToTk = (tkId: any) => {
  try {
    localStorage.setItem("lx:scrollToTask", String(tkId));
  } catch {}
};
export const checkScrollToTk = () => {
  try {
    const tkId = localStorage.getItem("lx:scrollToTask");
    if (tkId) {
      localStorage.removeItem("lx:scrollToTask");
      scrollToTk(+tkId || tkId);
    }
  } catch {}
};

/* Brief 42 follow-on — IS THIS BELL ROW READ. THE one predicate: every surface
   that asks must ask this, because the defect was three surfaces asking three
   different questions about one fact.
   Read state lives in TWO planes and that is by design, not by accident:
   · a PERSISTED row's plane is the server (`Notification.readAt`, mirrored to
     the `_alreadyRead` sidecar) — durable, shared across devices;
   · a DERIVED row has no server row to mark, so its plane is the per-user
     `prefs.notifRead` id list.
   The row is routed by `_persistedDbId`, THE SIDECAR IT ALREADY CARRIES, and
   never by sniffing the `"db-"` prefix off its id. That distinction is the
   whole point: a prefix test would have to be repeated at every call site and
   would be a second mechanism for a fact the row already states — which is
   what brief 35 §5c forbids and what this defect WAS.
   Lifted here from `CompactColumn`, which already had exactly this shape while
   the bell did not. It is not new logic; it is the tree's own answer, moved to
   one place so the three surfaces cannot drift again. */
/* The read/dismiss FACE of a bell row — only the fields these two mechanisms
   touch, never the whole row. Structural, so every surface's richer object
   satisfies it without being described here: the aggregator's rows carry a
   dozen more fields and none of them are this module's business. */
export type NotifRow = {
  id?: string | number | null;
  _persistedDbId?: number | null;
  _alreadyRead?: boolean;
  dismissId?: string | null;
  /* Brief 45 §2 — the rows this one collapsed, present only on a survivor.
     It belongs on THIS face and not merely in the aggregator, because read and
     dismiss are exactly the two mechanisms that must walk it: a survivor stands
     for its item, so clearing it has to clear every row the item has, or a
     superseded sibling comes back unread the moment the survivor goes. */
  _superseded?: NotifRow[];
};
export const isNotifRead = (
  n: NotifRow | null | undefined,
  notifRead: readonly unknown[] | null | undefined,
) =>
  n && n._persistedDbId != null
    ? !!n._alreadyRead
    : (notifRead || []).includes(n && n.id);

// Phase M+2b: SK.pm removed (lx:pm storage migrated/discarded earlier).
// Permissions now flow through usePermissions → /api/permissions/me; no
// localStorage cache key needed. Phase auth-c2: the one-shot scrub in
// App.jsx was also removed (legacy keys would be long gone from any
// active browser by now).
export const SK = {
  cl: "lx:cl",
  ca: "lx:ca",
  op: "lx:op",
  ocu: "lx:ocu",
  ex: "lx:ex",
  ju: "lx:ju",
  co: "lx:co",
  te: "lx:te",
  ag: "lx:ag",
  cp: "lx:cp",
  oc: "lx:oc",
  cu: "lx:cu",
  sfm: "lx:sfm",
  xtm: "lx:xtm",
  me: "lx:me",
  ar: "lx:ar",
  op2: "lx:op2",
  tk: "lx:tk",
  cc: "lx:cc",
  books: "lx:books",
  inv: "lx:inv",
};
// 58 §5a — SEVEN. The five-day week was the DATA MODEL, not a UI guard: a
// stored weeklySchedule is an array indexed by this list, so a firm whose
// Saturday is a working day had nowhere to put it. Widening the axis is what
// lets §5b's setting mean anything.
//
// NOTHING RENDERS DIFFERENTLY BY DEFAULT. DEFAULT_WEEKLY_SCHEDULE gives the two
// new days EMPTY slot arrays, so weeklyHours stays 40 and every figure derived
// from it is unchanged; what changes is that Saturday and Sunday now EXIST and
// can be painted.
export const SCHED_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
// The working week INSIDE that axis, by index. Used only to decide which days
// the default schedule pre-fills — never to hide a day.
export const SCHED_DEFAULT_WORKING = 5;
export const SCHED_START = 7;
export const SCHED_END = 20;
export const SCHED_SLOTS = (SCHED_END - SCHED_START) * 2;

// 58 §5c — THE CONSTRUCTION, extracted. The four constants above were already
// central; what was copied is the code that BUILDS from them, and the brief's
// own measurement is why no component is being built: a day strip is five
// lines and a weekend test is one, so a component would be ceremony around a
// call.
//
// EXTRACT, DO NOT IMPROVE. Both helpers reproduce their call sites exactly:
// `dayStrip` starts one day BEFORE the anchor, which is what every strip did
// (`i - 1`), and `isWeekendDow` is `dw === 0 || dw === 6` unchanged. §5a and
// §5b change what they are given, in their own commits.

/**
 * The N-day strip every matter list and the dashboard draw, anchored so that
 * index 0 is the day BEFORE `from`. Returns Dates, not strings: two of the
 * three call sites needed the Date for `getDay()` and the third formatted it.
 */
export const dayStrip = (from: Date, length = 14): Date[] =>
  Array.from({ length }, (_, i) => {
    const d = new Date(from);
    d.setDate(from.getDate() + (i - 1));
    return d;
  });

/** Saturday or Sunday, by `Date.getDay()`'s numbering (0 = Sunday). */
export const isWeekendDow = (dow: number): boolean => dow === 0 || dow === 6;

/**
 * `Date.getDay()` (0 = Sunday) → an index into `SCHED_DAYS` (0 = Monday). The
 * schedule axis starts on Monday and JavaScript's week does not; this was
 * written three different ways across the schedule views, one of which
 * (`dw - 1`) produced -1 for Sunday and was only safe because Sunday was
 * skipped before it could be asked.
 */
export const schedDayIndex = (dow: number): number => (dow + 6) % 7;

/** The same test on a Date. */
export const isWeekendDay = (d: Date): boolean => isWeekendDow(d.getDay());
// LOC_CLR / LOC_LBL / LOC_CYCLE retired in M+3.6 C5. Work-location colour
// + label now flow through the work_location taxonomy via
// getWorkLocationMeta / workLocationCycle in src/lib/taxonomyHelpers.js.
export const defaultDaySlots = () => {
  const a = new Array(SCHED_SLOTS).fill(null);
  for (let i = (9 - SCHED_START) * 2; i < (18 - SCHED_START) * 2; i++) {
    const h = SCHED_START + Math.floor(i / 2);
    const m = i % 2 === 0 ? 0 : 30;
    const t = h * 60 + m;
    if (t >= 12 * 60 + 30 && t < 13 * 60 + 30) continue;
    a[i] = "onsite";
  }
  return a;
};
export const DEFAULT_WEEKLY_SCHEDULE = SCHED_DAYS.map((d, i) => ({
  day: d,
  // 58 §5a — Saturday and Sunday exist and are EMPTY. Filling them with
  // defaultDaySlots() would have moved every collaborator from 40 hours to 56
  // without anybody asking for it.
  slots: i < SCHED_DEFAULT_WORKING ? defaultDaySlots() : new Array(SCHED_SLOTS).fill(null),
}));

/**
 * 58 §5a — NORMALISE ON READ. RULED by Laurent: no migration, there is no such
 * data yet. A stored five-entry array must not render five columns beside a
 * colleague's seven, so every reader pads to `SCHED_DAYS.length` at the point
 * it loads. A missing schedule is the default; a short one keeps its days and
 * gains empty ones; a long one is truncated, so a stale seven cannot outlive a
 * narrower axis either.
 */
export type ScheduleDay = { day: string; slots: (string | null)[] };
export const normalizeWeeklySchedule = (
  ws: readonly Partial<ScheduleDay>[] | null | undefined,
  // 58 §5b — the row axis is a firm setting too, so a stored day may hold more
  // or fewer half-hours than the firm now shows. Same rule as the day axis:
  // pad with empties, truncate the tail, touch no data.
  slotCount: number = SCHED_SLOTS,
): ScheduleDay[] => {
  const src = Array.isArray(ws) && ws.length ? ws : DEFAULT_WEEKLY_SCHEDULE;
  const fit = (slots: (string | null)[]) =>
    slots.length === slotCount
      ? slots
      : Array.from({ length: slotCount }, (_, i) => slots[i] ?? null);
  return SCHED_DAYS.map((d, i) => {
    const row = src[i];
    return row && Array.isArray(row.slots)
      ? { ...row, day: row.day || d, slots: fit(row.slots) }
      : { day: d, slots: new Array(slotCount).fill(null) };
  });
};
export const weeklyHours = (ws: any) => {
  if (!ws || !ws.length) return 40;
  return (
    Math.round(
      ws.reduce((s: any, d: any) => s + (d.slots || []).filter(Boolean).length * 0.5, 0) *
        10,
    ) / 10
  );
};
export const weeklyBreakdown = (ws: any) => {
  const b: any = { onsite: 0, thirdparty: 0, home: 0 };
  if (!ws || !ws.length) return { onsite: 40, thirdparty: 0, home: 0 };
  ws.forEach((d: any) =>
    (d.slots || []).forEach((loc: any) => {
      if (loc) b[loc] = (b[loc] || 0) + 0.5;
    }),
  );
  return {
    onsite: Math.round(b.onsite * 10) / 10,
    thirdparty: Math.round(b.thirdparty * 10) / 10,
    home: Math.round(b.home * 10) / 10,
  };
};
export const slotToTime = (i: any) => {
  const h = SCHED_START + Math.floor(i / 2);
  const m = i % 2 === 0 ? "00" : "30";
  return `${String(h).padStart(2, "0")}:${m}`;
};
export async function ld(k: any, f: any) {
  try {
    const r = await window.storage.get(k);
    return r ? JSON.parse(r.value) : f;
  } catch {
    return f;
  }
}
export async function sv(k: any, d: any) {
  try {
    await window.storage.set(k, JSON.stringify(d));
  } catch (e) {
    console.error(e);
  }
}
// Module-level cache for the current firm name — primed by App.jsx on the
// initial firmSettings load and on every saveFirmSettings({firm:{name}})
// write. Lets firmNameSlug() produce a filename-safe slug synchronously
// from event handlers (.ics download code, etc.) without drilling a
// firmName prop through three view trees. Backed by a module variable,
// NOT localStorage — lx:fname is no longer consulted for reads post-A2.
let _firmNameCache = "Legal Practice Manager";
export const setFirmNameCache = (name: any) => {
  _firmNameCache = name || "Legal Practice Manager";
};
// Filename-safe form of the cached firm name (the .ics download's first
// segment). 181 (ruling 1): a SPACE for what a name may not carry, not '_'.
// Falls back to "LegalPracticeManager" if unset.
export const firmNameSlug = () => {
  const s = (_firmNameCache || "")
    .replace(/[^A-Za-z0-9_-]+/g, " ")
    .trim();
  return s || "LegalPracticeManager";
};

// 05n — CASE_TYPES and STAGE_TYPES are gone, for COUNTRY_FALLBACK's reason.
// Both were bare-label fallbacks behind `list && list.length ? list : CONST`,
// and `useTaxonomyList` never returns an empty list for a kind that has a
// KIND_TO_DEFAULT — which both of these do. Unreachable before this slice;
// unreachable AND wrong-shaped after it. DOC_STATUSES below stays: its kind is
// `vlc`, unchanged here, and its fallback is reached the same way it always was.
export const DOC_STATUSES = [
  { v: "draft1", l: "First draft", c: CT.c_8892a4 },
  { v: "draft_final", l: "Final draft", c: CT.c_4a7cff },
  { v: "draft_approved", l: "Client approved", c: CT.c_f59e0b },
  { v: "filed", l: "Filed in court", c: CT.c_34d399 },
  { v: "sent", l: "Sent to opponent", c: CT.c_34d399 },
  { v: "received", l: "Received", c: CT.c_8892a4 },
  { v: "archived", l: "Archived", c: CT.c_8892a4 },
];
export const FT_INFO = {
  pdf: { l: "PDF", c: CT.c_f87171 },
  word: { l: "DOC", c: CT.c_4a7cff },
  excel: { l: "XLS", c: CT.c_34d399 },
  ppt: { l: "PPT", c: CT.c_fb923c },
  email: { l: "MSG", c: CT.c_a78bfa },
  image: { l: "IMG", c: CT.c_22d3ee },
  archive: { l: "ZIP", c: CT.c_8892a4 },
  video: { l: "VID", c: CT.c_f472b6 },
  text: { l: "TXT", c: CT.c_8892a4 },
  // 151 §2 — a pasted web link's file type IS "weblink" (ruled); the badge
  // exists so the §3 row can wear it like any other type.
  weblink: { l: "WWW", c: CT.c_8892a4 },
  other: { l: "···", c: CT.c_8892a4 },
};
export const detFT = (name: any) => {
  const e = (name || "").split(".").pop().toLowerCase();
  if (e === "pdf") return "pdf";
  if (["doc", "docx"].includes(e)) return "word";
  if (["xls", "xlsx"].includes(e)) return "excel";
  if (["ppt", "pptx"].includes(e)) return "ppt";
  if (["msg", "eml"].includes(e)) return "email";
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(e)) return "image";
  if (["zip", "rar", "7z"].includes(e)) return "archive";
  if (["mp4", "mov", "avi", "mkv"].includes(e)) return "video";
  if (e === "txt") return "text";
  return "other";
};
// Phase 8 B1+B2 — case-type pill style from TaxonomyEntry.meta.{color,bgColor}.
// Returns a style object (or null) to be spread on a <span className="tg">.
// Both color and bgColor are admin-tunable in Settings → Custom Lists → Case Types.
// Sites that have caseTypeEntries available pass them in; sites without entries
// pass undefined and the pill falls back to the .tg base grey.
export const ctc = (code: any, entries: any) => {
  if (!code || !entries || !Array.isArray(entries)) return null;
  // 05m step C — this used to carry its OWN three-facet copy of the matcher
  // (code / labelEn / label). Case.type stores a LABEL, in the language of
  // whoever saved the row, so a copy that did not know about labelFr and
  // labelNl silently returned no colour for exactly the rows the fork
  // produces. Prod holds one today — `CollectiveCase.type = "Financier"`
  // (census, 2026-08-03): a case type with an admin-chosen pill colour that
  // rendered grey because this lookup was three facets short of the one
  // sitting next to it in taxonomyHelpers. One matcher now.
  const e = resolveTaxonomyEntry(code, entries);
  const m = e && e.meta;
  if (!m || (!m.color && !m.bgColor)) return null;
  const out: any = {};
  if (m.bgColor) {
    out.background = m.bgColor;
    out.border = "0";
  }
  if (m.color) {
    out.color = m.color;
  }
  return out;
};
// Active locale (BCP-47) — kept in sync with the user's preferred language
// by I18nProvider via window.__lxLang. Helpers below read this so call
// sites that can't see React context (utility functions, non-component
// modules) still format dates / numbers in the user's locale.
const _LOCALE_BY_LANG = { en: "en-GB", fr: "fr-BE", nl: "nl-BE" };
const _activeLocale = () =>
  (_LOCALE_BY_LANG as any)[(typeof window !== "undefined" && window.__lxLang) || "en"] ||
  "fr-BE";
const _t = (k: any, v: any) =>
  typeof window !== "undefined" && window.__lxI18n
    ? window.__lxI18n.t(k, v)
    : (v && v.fallback) || k;
/**
 * 161B §1 — NO SURFACE EVER PRINTS `-€0.00`.
 *
 * `Intl` faithfully carries the sign of a negative value that rounds to zero,
 * so a total of −0.005 (or a literal −0) renders as a minus sign in front of
 * nothing. The ladder no longer PRODUCES that value (`netOfDeduction`,
 * server/src/lib/proformaCompute.js), but every row stored before that fix
 * still holds it, and formatting is the last gate they pass through.
 *
 * `Math.abs(n) < 0.005` subsumes `Object.is(n, -0)`: half a cent is exactly
 * the band that cannot round to a non-zero cent, so collapsing it changes no
 * digit that was going to be printed — only the sign in front of the zero.
 * Anything that is not a finite number falls through untouched, so callers
 * that hand `null`/`""` keep whatever they did with it.
 *
 * IT LIVES HERE BECAUSE THERE ARE THREE FORMATTERS, NOT ONE: this file's
 * `fc` (the context-free one), `i18n.tsx`'s `fc` from `useTranslation()`, and
 * the `tIn(lang)` variant that renders the PDF ladder in the document's own
 * language. Laurent saw `-€0.00` on all three surfaces; a fix in one would
 * have left the other two.
 */
export const noNegZero = (n: any) =>
  typeof n === "number" && Number.isFinite(n) && Math.abs(n) < 0.005 ? 0 : n;

/**
 * 169 R3 — THE FIRM'S CURRENCY, one reader in the browser: `window.__lxFirmCurrency`,
 * set by `noteFirmCurrency` where firmSettings lands in App (the `__lxLang` seam's
 * twin). Absent = EUR, which is every firm on this box today.
 */
const _lxGlobal = globalThis as unknown as { __lxFirmCurrency?: string };
export const firmCurrencyOf = (): string => _lxGlobal.__lxFirmCurrency ?? "EUR";
export const noteFirmCurrency = (fs: unknown) => {
  const firm = fs && typeof fs === "object" ? (fs as { firm?: unknown }).firm : null;
  const c = firm && typeof firm === "object" ? (firm as { currency?: unknown }).currency : null;
  _lxGlobal.__lxFirmCurrency = typeof c === "string" && /^[A-Z]{3}$/.test(c) ? c : "EUR";
};
/**
 * 207 §10 (i) [ADVISOR's reading — Laurent overrules at the glance] — MONEY ON
 * SCREEN PRINTS AS IN THE DOCUMENTS, the figure then « € », IN EVERY LANGUAGE:
 * « 13 553,33 € ». These two used to format in the READER's locale — en-GB
 * « €13,553.33 », nl-BE « € 13.553,33 » — while every document prints the
 * figure the one way (server/src/lib/docGen.js `moneyAmount`: fr-BE, two
 * decimals, then « € »). So the figure is fr-BE whatever the language, and a
 * currency other than EUR keeps its code after it, « 1 234,56 USD » (169 R8 —
 * the documents' own rule). `fq` is the same figure without the currency. The
 * two formatters `useTranslation()` hands out (i18n.tsx — the reader's and the
 * PDF ladder's `tIn(lang)`) delegate here, so there is one definition.
 */
const MONEY_FIGURE = new Intl.NumberFormat("fr-BE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
/** The documents' figure: fr-BE, two decimals (docGen.js `EUR_FMT`). */
export const moneyFigure = (n: unknown): string => MONEY_FIGURE.format(n as number);
/** The documents' amount: the figure, then « € » for EUR or the ISO code for any other currency. */
export const moneyText = (n: unknown, currency?: string | null): string => {
  const code = typeof currency === "string" && /^[A-Z]{3}$/.test(currency) ? currency : firmCurrencyOf();
  return `${moneyFigure(n)} ${code === "EUR" ? "€" : code}`;
};
export const fc = (n: any, currency?: string | null) => {
  try {
    return moneyText(noNegZero(n), currency);
  } catch {
    return String(n);
  }
};
export const fq = (n: any) => {
  if (n == null || n === "") return "—";
  try {
    return moneyFigure(+n);
  } catch {
    return String(n);
  }
};
export const fmin = (m: any) => {
  if (!m) return _t("time.zero_min", { fallback: "0 min" });
  const h = Math.floor(m / 60);
  const mn = m % 60;
  if (h > 0 && mn > 0)
    return _t("time.h_min", { h, mn, fallback: `${h}h ${mn}min` });
  if (h > 0) return _t("time.h_only", { h, fallback: `${h}h` });
  return _t("time.min_only", { mn, fallback: `${mn} min` });
};
// Real-time elapsed clock for running timers. Counts raw milliseconds into a
// live H:MM:SS (or M:SS under an hour) readout — no minute quantization, no
// "< 5 min" floor. The 5-min round-up lives ONLY in the Log-timer step.
export const fmtElapsed = (ms: any) => {
  const total = Math.max(0, Math.floor((ms || 0) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const p2 = (n: any) => ("0" + n).slice(-2);
  return h > 0 ? `${h}:${p2(m)}:${p2(s)}` : `${m}:${p2(s)}`;
};
export const evDayClr = (diff: any, type: any) =>
  diff === null
    ? "var(--tm)"
    : diff < 0
      ? "var(--tm)"
      : diff === 0
        ? "var(--rd)"
        : diff >= 1 && diff <= 3
          ? type === "DL"
            ? "var(--gn)"
            : "var(--am)"
          : "var(--tp)";
export const fd = (d: any) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString(_activeLocale(), {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};
// 05m step C — `country` is a taxonomy value on nine tables and was rendered
// raw here, so a client saved in Dutch showed "Nederland" in an English
// address block. Prod holds that exact pair: Netherlands and Nederland, one
// entry, two rows (census 2026-08-03). Same registry, same fallback as pname.
//
// §2 makes this load-bearing rather than cosmetic: once the vocabulary is ISO
// 3166 the column holds `BE`, and an unresolved render would print a two-letter
// code into a postal address.
export const fcountry = (v: TaxValue) =>
  resolveTaxonomyLabel(v, taxonomyEntriesOf("country"));
export const faddr = (i: any) =>
  [
    i.address || i.street,
    [i.postalCode, i.city].filter(Boolean).join(" "),
    fcountry(i.country),
  ]
    .filter(Boolean)
    .join(", ") || "—";
// P20a — the agenda modal's location autofill, resolved from event_type
// taxonomy DATA (the entry's meta.location) instead of the former
// genAgLocation ladder, which named Belgian legal event codes (INTRO/AUD/PRO
// → the case's jurisdiction, CCL/REQ/APP/748/747-8/VIR → "Online", REU → the
// firm room) inside a generic view. Any firm now retunes its own autofill.
//
// `spec` is one entry's meta.location, as eventLocationsFromEntries folds it
// (src/utils/taxonomyMap.ts). `place` is the case's linked place — today its
// jurisdiction row, passed already-resolved so this stays a pure function of
// the spec plus its context; null when the case carries none, which is the
// same "" the old ladder produced for a case with no juId. A spec-less code
// autofills nothing.
export const resolveEventLocation = (
  spec: any,
  ctx: { place?: any; firmRoom?: any } = {},
) => {
  if (!spec || !spec.mode) return "";
  if (spec.mode === "static") return spec.value || "";
  if (spec.mode === "firmRoom") return ctx.firmRoom || "";
  if (spec.mode === "linkedPlace") {
    const p = ctx.place;
    if (!p) return "";
    const addr = faddr(p);
    return addr && addr !== "—" ? p.name + ", " + addr : p.name;
  }
  return "";
};
// 05m step C — the person title is a TAXONOMY value and was rendered raw, so
// a client imported or saved in French read "Mme Jean Dupont" to an English
// reader. It resolves now, through the registry rather than through a prop:
// this function has 194 call sites and the entries were already in memory.
//
// AND IT IS WHAT LETS 05n LAND. After the migration `i.title` holds `mr`, not
// `Mr.` — raw would print `mr Jean Dupont` on 780 prod rows. Resolution is the
// precondition for the conversion, not a polish pass over it.
//
// Falls back to the stored string on every miss (kind not loaded, value the
// vocabulary does not contain, or an object whose `title` is a free-text
// subject rather than a person title), which is exactly today's output.
// 05m step C — the document category, in the two shapes its readers want.
//
// The colour ternary branched on the stored value against the literal
// "pleading", and it worked for exactly one reason: the six bundled entries
// have `labelEn === code`. That invariant is ALREADY false of the seventh —
// `mission_letter` carries labelEn "Mission letter" — and it goes false in
// every language the moment a firm's document rows exist, because labelFr is
// "Conclusions". Branch on the CODE the entry resolves to; the label is a
// display concern and never a condition.
//
// Latent rather than live, and named as such: prod's census (2026-08-03)
// shows `Document.type` with no rows at all, so this styles nothing today. It
// fires on the first typed document. Brief 22 §2 called it live; it is not,
// and the fix is the same either way.
// ONE binding site, not four. The ternary was duplicated verbatim in App.tsx
// and CaseView.tsx; folding it here means the codes this kind binds are
// declared in a single place the classification lint can attribute — wrapping
// the operand in a call at the call site made the binding INVISIBLE to its
// comparison detector, which is a worse outcome than the defect.
export const dcatCode = (v: TaxValue) => {
  const e = resolveTaxonomyEntry(v, taxonomyEntriesOf("document_category"));
  return e ? e.code : String(v || "");
};
export const dcatTag = (v: TaxValue) => {
  const e = resolveTaxonomyEntry(v, taxonomyEntriesOf("document_category"));
  return e?.code === "pleading"
    ? "tag-accent"
    : e?.code === "evidence"
      ? "tag-amber"
      : "tag-green";
};
export const dcatLabel = (v: TaxValue) =>
  String(resolveTaxonomyLabel(v, taxonomyEntriesOf("document_category")) || "")
    .replace("_", " ");
// The person title alone, for the call sites that render it apart from the
// name. Declared before `pname` so neither depends on evaluation order.
// 206E §4.3 — WIDENED (rule 4) into `ptitleIn` / `pnameIn`, which take a
// language (a pro forma's name is written in the CLIENT's). `pname` stays a
// ONE-argument function over `pnameIn`, because it travels as a value
// (`name: pname`, `pname={pname}`) and a second parameter could catch an array
// index from a point-free call downstream; `ptitle` keeps its own line. Without
// a language, both are exactly what they were.
export const ptitle = (v: TaxValue) =>
  resolveTaxonomyLabel(v, taxonomyEntriesOf("person_title"));
export const ptitleIn = (v: TaxValue, lang?: string): unknown =>
  resolveTaxonomyLabel(v, taxonomyEntriesOf("person_title"), lang);
export const pnameIn = (i: any, lang?: string) =>
  i.companyName ||
  [ptitleIn(i.title, lang), i.firstName, i.lastName].filter(Boolean).join(" ") ||
  i.name ||
  "—";
export const pname = (i: unknown): string => pnameIn(i) as string;
// Phase 29 C63 — collaborator initials, the app-wide single source: the
// stored admin-editable `initials` column wins; blank falls back to the
// derived firstName[0]+lastName[0] (same rule as InitialsBadge and the
// server's lib/initials.js). Safe on non-collaborator persons too — they
// carry no `initials` field, so the fallback reproduces the old derivation.
export const coIni = (c: any) =>
  (
    String(c?.initials || "").trim() ||
    (c?.firstName?.[0] || "") + (c?.lastName?.[0] || "")
  ).toUpperCase();
export const nid = (a: any) => (a.length ? Math.max(...a.map((x: any) => x.id)) + 1 : 1);
export const pm = (d: any) => {
  const lang = (typeof window !== "undefined" && window.__lxLang) || "en";
  const locale = lang === "fr" ? "fr-BE" : lang === "nl" ? "nl-BE" : "en-GB";
  return new Date(d)
    .toLocaleDateString(locale, { month: "short" })
    .toUpperCase();
};
// Phase auth-c2 Step 6 — ROLE_RATIO removed. Replaced by Role.defaultRatio
// (DB column), surfaced via /api/roles/labels and consumed via
// roleDefaultRatio(name) from useRoleLabels. The map values that lived
// here are now seeded into Role.defaultRatio by
// scripts/seed_migration_phase_auth_c2_role_default_ratio.js.
export const DEFAULT_EXT_TYPES = [
  { ext: "pdf", type: "pleading" },
  { ext: "docx", type: "correspondence" },
  { ext: "doc", type: "correspondence" },
  { ext: "odt", type: "correspondence" },
  { ext: "xlsx", type: "other" },
  { ext: "xls", type: "other" },
  { ext: "pptx", type: "other" },
  { ext: "jpg", type: "evidence" },
  { ext: "jpeg", type: "evidence" },
  { ext: "png", type: "evidence" },
  { ext: "gif", type: "evidence" },
  { ext: "mp4", type: "evidence" },
  { ext: "mov", type: "evidence" },
  { ext: "zip", type: "other" },
  { ext: "txt", type: "other" },
];
export const TASK_STATUSES = [
  ["todo", "To do"],
  // Round 13 (C78) — canonical EN label is "Ongoing" (shorter row chip).
  ["inprogress", "Ongoing"],
  ["waiting", "Waiting"],
  ["completed", "Completed"],
  ["cancelled", "Cancelled"],
  ["archived", "Archived"],
];
export const REMINDER_OPTS = [
  ["none", "None"],
  ["0", "At due date"],
  ["1", "1 day before"],
  ["2", "2 days before"],
  ["3", "3 days before"],
  ["7", "1 week before"],
  ["14", "2 weeks before"],
  ["30", "1 month before"],
];
export const reminderDate = (dueDate: any, days: any) => {
  if (!dueDate || days === "none" || days == null) return null;
  const d = new Date(dueDate);
  d.setDate(d.getDate() - Number(days));
  return d.toISOString().slice(0, 10);
};
// The firm's ladder: any number of levels, each {v,l,c} (v=code, l=label,
// c=colour), ORDERED. The taxonomy is the only source — there is no default
// ladder here, and no offline fallback, because a fallback is a second source
// and the two disagreed for months. When the list is empty the helpers return
// empty maps and every call site falls to `provisionalLevel`, which shows the
// stored CODE muted: provisional, and legibly so.
// A rung as the taxonomy hands it over (toDisplayList's `vlc` shape), and a
// rung as a badge renders it. Typed rather than `any` so a call site indexing
// the map needs no cast — the ratchet is the reason, and the reason is good.
export type LevelRung = { v: string; l?: string; c?: string };
export type LevelBadge = { l: string; c: string; provisional?: boolean };
export type LevelList = readonly LevelRung[] | null | undefined;

export const itemLevelMap = (list: LevelList): Record<string, LevelBadge> => {
  const arr = list && list.length ? list : [];
  const m: Record<string, LevelBadge> = {};
  for (const it of arr) {
    if (it && it.v) m[it.v] = { l: it.l || "", c: it.c || "var(--tm)" };
  }
  return m;
};
// The firm's ladder has any number of levels and only ONE source of order:
// the taxonomy's `order` column. useTaxonomy already sorts entries by it
// (useTaxonomy.ts:211), so the list arrives ascending and the array INDEX is
// the ladder position — nothing below re-reads `order`, and nothing names a
// code. On the shipped [low, medium, high] list these reproduce the old
// hardcoded `{ low: 1, medium: 2, high: 3 }` exactly.
export const itemLevelOrder = (list: LevelList): Record<string, number> => {
  const arr = list && list.length ? list : [];
  const m: Record<string, number> = {};
  arr.forEach((it, i) => {
    if (it && it.v) m[it.v] = i + 1;
  });
  return m;
};
// The bottom of the ladder — used for SORTING ONLY: an item with no level has
// to rank somewhere, and it ranks at the bottom.
//
// It is NOT a create default and there is no longer any such thing. 18b §1
// ruled that a new item opens and saves UNSET, on the same argument slice 18
// used to delete the due-date inference: creating an item says nothing about
// whether it is dominus litis or administrative. The default that existed here
// was an artefact of when this field meant urgency, where "medium" was a
// sensible neutral. For a decision level there is no neutral — AD, AS and DL
// are three different people. `itemLevelDefault` is deleted rather than kept
// for a caller that might return.
export const itemLevelBase = (list: LevelList): string | null =>
  (list && list.length ? list[0]?.v : null) ?? null;
// A code the ladder does not resolve — the taxonomy has not loaded yet, or a
// level was deleted out from under a row. Shows the CODE, muted: provisional
// and legibly so, never a confident letter borrowed from a ladder that is not
// this firm's.
export const provisionalLevel = (code: unknown): LevelBadge => ({
  l: String(code ?? "")
    .slice(0, 3)
    .toUpperCase(),
  c: "var(--tm)",
  provisional: true,
});
export const DARK_THEME_VARS = {
  "--bd": "#0c0f14",
  "--bs": "#141820",
  "--bc": "#1a1f2b",
  "--bh": "#1f2537",
  "--bi": "#161b26",
  "--br": "#252d3a",
  "--tp": "#e8ecf4",
  "--ts": "#8892a4",
  "--tm": "#5a6478",
  "--ac": "#4a7cff",
  "--as": "rgba(74,124,255,.12)",
  "--ag": "rgba(74,124,255,.25)",
  "--gn": "#34d399",
  "--am": "#fbbf24",
  "--rd": "#f87171",
  "--or": "#fb923c",
  "--ct": "#e0e4ea",
  "--bf": "rgba(200,210,220,.7)",
  "--gnm": "#5a9d80",
  "--rdm": "#cf7a7a",
};
// M+3.6 C9 — Light --tp is the canonical #1f2937. Derived --ts / --tm
// pull toward --bs (the original "muted = less prominent than primary"
// direction), with reduced coefficients on light surfaces so derived
// --tm clears AA-large 3:1 against --bh / --bc with margin. Admin can
// override --ts and --tm per-theme via the editor when defaults aren't
// satisfactory.
export const LIGHT_THEME_VARS = {
  "--bd": "#f0f2f5",
  "--bs": "#ffffff",
  "--bc": "#e8eaed",
  "--bh": "#dfe1e5",
  "--bi": "#f5f6f8",
  "--br": "#d1d5db",
  "--tp": "#1f2937",
  "--ts": "#6b7280",
  "--tm": "#9ca3af",
  "--ac": "#4a7cff",
  "--as": "rgba(74,124,255,.10)",
  "--ag": "rgba(74,124,255,.20)",
  "--gn": "#34d399",
  "--am": "#fbbf24",
  "--rd": "#f87171",
  "--or": "#ea580c",
  "--ct": "#1f2937",
  "--bf": "rgba(71,85,105,.7)",
  "--gnm": "#4e8a70",
  "--rdm": "#b86464",
};
export const BLUE_THEME_VARS = {
  "--bd": "#f0f4fa",
  "--bs": "#1B2A4A",
  "--bc": "#233456",
  "--bh": "#2b3e62",
  "--bi": "#e8eef6",
  "--br": "#334d73",
  "--tp": "#1f2937",
  "--ts": "#6b7280",
  "--tm": "#94a3b8",
  "--ac": "#2563EB",
  "--as": "rgba(37,99,235,.12)",
  "--ag": "rgba(37,99,235,.25)",
  "--gn": "#34d399",
  "--am": "#fbbf24",
  "--rd": "#f87171",
  "--or": "#ea580c",
  "--ct": "#1f2937",
  "--bf": "rgba(71,85,105,.7)",
  "--gnm": "#5a9d80",
  "--rdm": "#cf7a7a",
  "--stp": "#e2e8f0",
  "--sts": "#94a3b8",
  "--stm": "#64748b",
  "--sbr": "#334d73",
};
export const DEFAULT_THEME_PRESETS = [
  { id: "dark", name: "Dark", isDefault: true, vars: DARK_THEME_VARS },
  { id: "light", name: "Light", isDefault: true, vars: LIGHT_THEME_VARS },
  { id: "blue", name: "Blue", isDefault: true, vars: BLUE_THEME_VARS },
];
export const contrastText = (hex: any) => {
  if (!hex || !hex.startsWith("#")) return CT.c_e8ecf4;
  const r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.5 ? CT.c_1f2937 : CT.c_e8ecf4;
};
export const LINK_ICON_OPTIONS = [
  "HardDrive",
  "Cloud",
  "Folder",
  "Globe",
  "FileText",
  "Link",
  "Database",
  "Archive",
  "ExternalLink",
  "BookOpen",
  "Paperclip",
  "Server",
];
export const accentToShades = (hex: any) => {
  const r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);
  return {
    "--as": `rgba(${r},${g},${b},.12)`,
    "--ag": `rgba(${r},${g},${b},.25)`,
  };
};
// Returns the internal cost rate (EUR/hour) for a collaborator on a given date.
// Picks the row with the most recent startDate <= the supplied date.
// Returns 0 if the collaborator has no rates or none are effective on that date.
export const getInternalRate = (collaborator: any, date: any) => {
  const rs =
    collaborator && Array.isArray(collaborator.internalRates)
      ? collaborator.internalRates
      : null;
  if (!rs || !rs.length || !date) return 0;
  const d =
    typeof date === "string"
      ? date.slice(0, 10)
      : new Date(date).toISOString().slice(0, 10);
  const eligible = rs.filter((r: any) => r && r.startDate && r.startDate <= d);
  if (!eligible.length) return 0;
  eligible.sort((a: any, b: any) => b.startDate.localeCompare(a.startDate));
  return +eligible[0].rate || 0;
};
// Walk stages in reverse and return the first stage object that carries a
// register number. Treats the stages array as an append-only journal where
// "later in the array" means "more recent", so users get the register
// number from the most recent stage they've entered regardless of whether
// startDate is filled in.
export const getLatestRegStage = (cs: any) => {
  if (!cs || !Array.isArray(cs.stages)) return null;
  for (let i = cs.stages.length - 1; i >= 0; i--) {
    const s = cs.stages[i];
    if (s && s.registerNumber) return s;
  }
  return null;
};
export const getLatestRegNum = (cs: any) => {
  const st = getLatestRegStage(cs);
  return st ? st.registerNumber || "" : "";
};
// BRIEF 86 PHASE 2 STEP 1 — `visibleTimeEntries` DELETED, and it had been half
// dead since Phase M+2b. That phase established that the SERVER already trims
// `te` to what the viewer may see (`filterAndShapeTimeEntries`), called this
// filter "belt-and-suspenders dead weight" and dropped CaseView's CALL — but
// left the import, and CollectiveCaseView kept calling it. Merging the two
// sections removed the last caller, so the function became reachable from
// nothing at all and goes with it.
//
// Deleted rather than recorded: FIX IT OR FORGET IT (ruled 2026-08-17) makes a
// dead export found inside a unit that unit's own work, and a note about it
// would be a register row by another name. Its behaviour is not lost — it is
// what the server now performs, on rows the client is no longer trusted to
// filter.
// Phase auth-c2 — was a hardcoded ADMIN_PARTNER_ROLES check; now consults
// the `rates.internal.edit` section key via the permissions hook. Admin
// overlay short-circuits. Pre-auth path (perms===null/undefined) returns
// true to mirror the legacy "no currentUser → permissive" branch used at
// boot before /permissions/me has resolved.
export const canEditInternalRates = (perms: any) => {
  if (!perms) return true;
  if (perms.viewer?.isAdmin) return true;
  return typeof perms.can === "function" && perms.can("rates.internal.edit");
};
// Phase auth-c2 — role billing-attribute readers.
//
// `isFeeEarnerCollab(c)`  — true when the collaborator's role is a
//   fee-earner (counts as "partner fees" in the cost-report split,
//   excluded from internal-cost aggregation). Source of truth is the
//   Role.isFeeEarner column; the role-name fallback (`role==='Partner'`)
//   stays active for one deploy so pre-migration DBs preserve the prior
//   semantics. Drop the fallback in a follow-up commit once the C2
//   migration + seed have been applied across every environment.
//
// AUTHORITY (155 §4): for anything PERSISTED, the server's verbatim copy in
// server/src/lib/proformaCompute.js governs (admin-truth inputs, frozen by
// the c2-ladder recording); this copy serves display-side fee math and must
// stay character-identical to it.
// `billsAtZeroCollab(c)` — true when the collaborator's role bills at €0
//   (effective hourly rate = 0). Source of truth is Role.billsAtZero;
//   role-name fallback for Secretary / Executive Secretary, same
//   transitional-window rationale.
export const isFeeEarnerCollab = (c: any) => {
  if (!c) return false;
  if (c.isFeeEarner === true) return true;
  return c.role === "Partner";
};
export const billsAtZeroCollab = (c: any) => {
  if (!c) return false;
  if (c.billsAtZero === true) return true;
  return c.role === "Secretary" || c.role === "Executive Secretary";
};
// Eisenhower matrix — auto-placement by activity type, plus manual override stored on task.eisenhower
//
// The default quadrant per activity code is taxonomy DATA (the activity_code
// entry's meta.eisen), not a constant: the former EISEN_AUTO_UI / EISEN_AUTO_I
// lists baked legal activity codes into a generic view. Any firm can now retune
// its own defaults from Settings → Custom Lists.
//
// eisenAuto is a pure string→quadrant function called from non-React sites
// (eisenQuadrant, sort comparators), so it reads the loaded taxonomy the way
// eisenLabel(q) reads window.__lxEisen (Phase M+3.7): App.tsx publishes the
// { code: quadrant } fold as window.__lxActEisen, and — exactly as there — a
// fresh value is picked up on the next render. Before that map exists (first
// paint), the bundled activity_code defaults answer instead, which is the same
// data the seed wrote, so the resolved quadrant is identical either way.
// A code with no default resolves to "s".
export const eisenAuto = (type: any) => {
  if (!type) return "s";
  const loaded =
    typeof window !== "undefined" && window.__lxActEisen;
  if (loaded) return loaded[type] || "s";
  const d = (KIND_TO_DEFAULT.activity_code || []).find(
    (x: any) => x.code === type,
  );
  return (d && d.eisen) || "s";
};
export const eisenQuadrant = (t: any) =>
  (t && (t.caseEisenhower || t.eisenhower)) || eisenAuto(t && t.type);
export const EISEN_QUADRANTS = [
  {
    k: "ui",
    label: "Urgent & Important",
    bg: "rgba(248,113,113,.10)",
    border: "rgba(248,113,113,.35)",
    accent: CT.c_f87171,
  },
  {
    k: "u",
    label: "Urgent",
    bg: "rgba(251,191,36,.10)",
    border: "rgba(251,191,36,.35)",
    accent: CT.c_fbbf24,
  },
  {
    k: "i",
    label: "Important",
    bg: "rgba(74,124,255,.10)",
    border: "rgba(74,124,255,.35)",
    accent: CT.c_4a7cff,
  },
  {
    k: "s",
    label: "Spare time",
    bg: "rgba(140,140,140,.10)",
    border: "rgba(140,140,140,.30)",
    accent: CT.c_8892a4,
  },
];
// 190 round 7 [AF] — RULED, Laurent 2026-09-16 ("take a 'light' color for all
// side colors … less visual clutter"): EVERY CARD'S LEFT EDGE IS THE LIGHT
// TINT OF ITS COLOUR — the Eisenhower matrix's cards (TaskMatrix), the two task
// Bentos and the Eisenhower dialog (EisenTaskCard), the list Bentos' matter
// cards (CaseBoardView). The identity is unchanged — the quadrant's accent
// above, resolved by `userMatrixQuadrant`; a matter card's `--ac`; a
// collective case's `--gn` — only the weight drops. ONE derivation, declared
// here and read by every card, so a sixth surface later cannot pick a
// different lightness. The recipe is round 6 [S](ii)'s collective tint,
// `color-mix` toward the card's own ground (`--bc`) — the tree's idiom (App.css
// `--rule`, ScheduleGrid) — now for every edge; no token invented.
export const edgeTint = (accent: string) => `color-mix(in srgb, ${accent} 45%, var(--bc))`;

// Phase 21.2 (D) — single source of truth for a toast variant derived from an
// operation's OUTCOME counts, not from HTTP 200. A bill/save that created/saved
// nothing (e.g. every item was already a duplicate) or had failures must NOT
// render as green success. Pass this as the toast `type` at every fetch/save/
// bill call site so a failure can never render green again.
//
//   ok        — count actually saved/created (the only thing that earns green)
//   duplicate — count already-present / skipped-as-existing / not-attempted
//   failed    — count that errored
//
//   success (green) ⇔ ok > 0 AND no duplicate AND no failed
//   error   (red)   ⇔ ok === 0            (nothing saved/created, or all failed)
//   warning (amber) ⇔ ok > 0 AND (duplicate > 0 OR failed > 0)   (mixed/partial)
export function outcomeVariant({ ok = 0, duplicate = 0, failed = 0 } = {}) {
  if ((Number(ok) || 0) <= 0) return "error";
  if ((Number(failed) || 0) > 0 || (Number(duplicate) || 0) > 0) return "warning";
  return "success";
}
